/**
 * Verifier API Client
 *
 * Provides methods to interact with the Verifier API endpoints for reviewing
 * and approving AI-generated damage reports.
 *
 * Sprint: Sprint 12
 * Story: US-API - Verifier Dashboard UI (5 points)
 */

import apiClient from '../api-client';

// ==================== Types ====================

export interface JobSummary {
  job_id: string;
  job_number: string;
  customer_name?: string;
  vehicle_info: string;
  status: string;
  max_severity: string;
  damage_count: number;
  assigned_at: string;
  created_at: string;
}

export interface JobListResponse {
  jobs: JobSummary[];
  total: number;
  pending_count: number;
  completed_today: number;
}

export interface DamageAssessment {
  id: string;
  damage_type: string;
  severity: string;
  location: string;
  confidence_score: number;
  description?: string;
  estimated_cost?: number;
  created_at: string;
}

export interface AnnotatedImage {
  id: string;
  file_name: string;
  url: string;
  thumbnail_url?: string;
  damage_markers: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
    damage_id: string;
  }>;
}

export interface WorkflowHistoryItem {
  id: string;
  action: string;
  verifier_id?: string;
  comments?: string;
  created_at: string;
}

export interface ReviewDetailsResponse {
  job: {
    id: string;
    job_number: string;
    status: string;
    customer: {
      first_name: string;
      second_name: string;
      email?: string;
      phone?: string;
    };
    vehicle: {
      make: string;
      model: string;
      year: number;
      registration: string;
    };
    created_at: string;
  };
  damage_assessments: DamageAssessment[];
  annotated_images: AnnotatedImage[];
  workflow_history: WorkflowHistoryItem[];
  summary: {
    total_damages: number;
    max_severity: string;
    total_estimated_cost: number;
    ai_confidence_avg: number;
  };
}

export interface ApproveReportRequest {
  comments?: string;
}

export interface ApproveReportResponse {
  status: string;
  workflow_id: string;
  message: string;
}

export interface RejectReportRequest {
  reason: string;
  requested_photos?: string[];
}

export interface RejectReportResponse {
  status: string;
  workflow_id: string;
  message: string;
}

export interface DamageModification {
  assessment_id?: string;
  action: 'update' | 'delete' | 'add';
  data?: {
    damage_type?: string;
    severity?: string;
    location?: string;
    description?: string;
    estimated_cost?: number;
  };
}

export interface ModifyDamagesRequest {
  modifications: DamageModification[];
  comments?: string;
}

export interface ModifyDamagesResponse {
  status: string;
  workflow_id: string;
  modifications_applied: number;
  message: string;
}

// ==================== API Client ====================

class VerifierApi {
  /**
   * Get list of jobs assigned to current verifier
   */
  async getAssignedJobs(statusFilter?: string): Promise<JobListResponse> {
    const params = statusFilter ? { status: statusFilter } : undefined;
    return apiClient.get<JobListResponse>('/api/v1/verifier/jobs', params);
  }

  /**
   * Get complete review details for a job
   */
  async getReviewDetails(jobId: string): Promise<ReviewDetailsResponse> {
    return apiClient.get<ReviewDetailsResponse>(`/api/v1/verifier/reviews/${jobId}`);
  }

  /**
   * Approve a damage report
   */
  async approveReport(
    jobId: string,
    request: ApproveReportRequest
  ): Promise<ApproveReportResponse> {
    return apiClient.post<ApproveReportResponse>(
      `/api/v1/verifier/reviews/${jobId}/approve`,
      request
    );
  }

  /**
   * Reject a damage report and request more photos
   */
  async rejectReport(
    jobId: string,
    request: RejectReportRequest
  ): Promise<RejectReportResponse> {
    return apiClient.post<RejectReportResponse>(
      `/api/v1/verifier/reviews/${jobId}/reject`,
      request
    );
  }

  /**
   * Modify damage assessments (update, delete, or add)
   */
  async modifyDamages(
    jobId: string,
    request: ModifyDamagesRequest
  ): Promise<ModifyDamagesResponse> {
    return apiClient.put<ModifyDamagesResponse>(
      `/api/v1/verifier/reviews/${jobId}/damages`,
      request
    );
  }

  /**
   * Get workflow history for a job
   */
  async getWorkflowHistory(jobId: string): Promise<WorkflowHistoryItem[]> {
    return apiClient.get<WorkflowHistoryItem[]>(`/api/v1/verifier/reviews/${jobId}/history`);
  }
}

// Export singleton instance
export const verifierApi = new VerifierApi();
export default verifierApi;
